import { useEffect, useRef, useState, FC, useCallback } from 'react';

import { GridColumn, GridRow, useBreakpoint } from '@hh.ru/magritte-ui';
import { useSelectorNonNullable } from '@hh.ru/redux-create-reducer';

import getDataQa from 'src/utils/getDataQa';
import CardProfessionGraph from 'src/widgets/CareerTrack/CardProfessionGraph';
import CurvyArrow, { CurvyArrowProps } from 'src/widgets/CareerTrack/CurvyArrow';
import { getTagStyleByIndex } from 'src/widgets/CareerTrack/utils';

import styles from './styles.less';

interface GraphCareerTrackProps {
    selectedProfessionIndex: number | null;
    onSelectProfession: (index: number | null) => void;
}

const DATA_QA_BLOCK = 'career-graph';

const GraphCareerTrack: FC<GraphCareerTrackProps> = ({ selectedProfessionIndex, onSelectProfession }) => {
    const sourceRef = useRef<HTMLDivElement>(null);
    const hostRef = useRef<HTMLDivElement>(null);
    const targetRefs = useRef<HTMLDivElement[]>([]);
    const [arrowListProps, setArrowListProps] = useState<CurvyArrowProps[]>([]);

    const { isXS, isS } = useBreakpoint();
    const isMobile = isXS || isS;

    const { sourceProfession, targetProfessionList } = useSelectorNonNullable((state) => state.widgetCareerTrack);

    const handleRecalc = useCallback(() => {
        if (!sourceRef.current || !hostRef.current || !targetRefs.current) {
            return;
        }
        setArrowListProps(
            targetRefs.current.map(
                (ref) =>
                    ({
                        start: sourceRef.current,
                        host: hostRef.current,
                        end: ref,
                    }) as CurvyArrowProps
            )
        );
    }, []);

    useEffect(() => {
        handleRecalc();
    }, [handleRecalc]);

    const handleTargetClick = (i: number) => {
        if (selectedProfessionIndex === i) {
            onSelectProfession(null);
            return;
        }
        onSelectProfession(i);
    };

    useEffect(() => {
        window.addEventListener('resize', handleRecalc);
        window.addEventListener('scroll', handleRecalc);

        return () => {
            window.removeEventListener('resize', handleRecalc);
            window.removeEventListener('scroll', handleRecalc);
        };
    }, [handleRecalc]);

    return (
        <div ref={hostRef}>
            <GridRow>
                <GridColumn xs={0} s={0} m={3} xl={2} flexibleContent>
                    <div className={styles.source}>
                        <CardProfessionGraph
                            ref={sourceRef}
                            checked={selectedProfessionIndex !== null}
                            readOnly
                            profession={sourceProfession}
                            sticky
                            data-qa={getDataQa(DATA_QA_BLOCK, 'source', sourceProfession.id)}
                        />
                    </div>
                </GridColumn>
                <GridColumn xs={0} s={0} m={1}>
                    {!isMobile &&
                        targetProfessionList.map(
                            (_, i) =>
                                arrowListProps[i] && (
                                    <CurvyArrow
                                        key={i}
                                        {...arrowListProps[i]}
                                        isActive={selectedProfessionIndex === i}
                                    />
                                )
                        )}
                </GridColumn>
                <GridColumn xs={4} s={8} m={4} xl={5}>
                    <div className={styles.targetList}>
                        {targetProfessionList.map((target, i) => (
                            <CardProfessionGraph
                                key={i}
                                ref={(ref) => (targetRefs.current[i] = ref!)}
                                checked={selectedProfessionIndex === i}
                                tagStyle={getTagStyleByIndex(targetProfessionList.length, i)}
                                onChange={() => handleTargetClick(i)}
                                probability={target.probability}
                                profession={target.profession}
                                data-qa={getDataQa(DATA_QA_BLOCK, 'target', target.profession.id)}
                            />
                        ))}
                    </div>
                </GridColumn>
            </GridRow>
        </div>
    );
};

export default GraphCareerTrack;
